import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelList } from '@components/channel-list';
import { DEBOUNCE_VARIANT_DARK } from '@components/debounced-input-search/src/debounced-input-search.type';
import { SearchItemModal } from '@components/modals';
import { SubmenuIcon } from '@components/submenu-with-icon';
import { DATE_FORMAT_LONG_DATE_WITH_TIME } from '@constants/datetime';
import { useSortable } from '@dnd-kit/sortable';
import { ADMISSION_STATUS_HIDDEN, ADMISSION_STATUS_IN_REVIEW, ADMISSION_STATUS_ON_SALE } from '@domain/admission';
import { Channel, EventChannel } from '@domain/channel';
import { useGetChannels } from '@hooks/events/use-get-event-channels';
import { useGetActiveChannels } from '@hooks/use-active-channels';
import { useGetSharePaymentLink } from '@hooks/use-get-share-payment-link/use-get-share-payment-link';
import { useQueryClient } from '@tanstack/react-query';
import { formatNumber } from '@utils/format-number/format-number';
import { fromBackend } from '@utils/map-date/from-backend';

import { AdmissionCardInfo } from '../components/admission-card-info';

import { Props } from './admission-card.type';

// eslint-disable-next-line complexity
export const AdmissionCard = ({
  id,
  timezone,
  name,
  description,
  quantity,
  soldItems,
  currency,
  isPublished,
  isPrivate,
  needsUserVerified,
  amount,
  onlinePrice,
  ddpPrice,
  salesEnds,
  status,
  tags,
  color,
  defaultChannel,
  showSubmenuIcon = true,
  onEdit,
  onDelete,
  onDuplicate,
  onClick,
  onChange,
  onUnassign,
  guestCapacity,
  isPreset,
  isPastEvent,
  checked,
  isMultiSelectActive,
  deleteIsUnassign,
  eventId,
  accessValidity,
}: Props) => {
  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id,
  });
  const isDateVisible = !!salesEnds && status === ADMISSION_STATUS_ON_SALE;
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const locale = i18n.language;
  const formattedPrice = formatNumber({
    locale,
    currency,
    number: amount,
    minimumFractionDigits: 0,
  });
  const formattedOnlinePrice = formatNumber({
    locale,
    currency,
    number: onlinePrice ?? 0,
    minimumFractionDigits: 0,
  });
  const formatedDdpPrice = formatNumber({
    locale,
    currency,
    number: ddpPrice ?? 0,
    minimumFractionDigits: 0,
  });

  const salesEndDate = fromBackend(salesEnds);
  const formattedSalesEndsDate = salesEndDate.tz(timezone)?.locale(locale).format(DATE_FORMAT_LONG_DATE_WITH_TIME);
  const isOpenForAll = isPublished && !isPrivate && !needsUserVerified;
  const [isOpenSelectPaymentLinkChannelModal, setIsOpenSelectPaymentLinkChannelModal] = useState(false);
  const [selectedPaymentLinkChannel, setSelectedPaymentLinkChannel] = useState<Channel | undefined>();
  const [searchPaymentLinkChannelsValue, setSearchPaymentLinkChannelsValue] = useState('');
  const [isSharedSubMenuClicked, setIsSharedSubMenuClicked] = useState(false);
  const [paymentLinkChannels, setPaymentLinkChannels] = useState<EventChannel[] | undefined>();

  const { isShareLinkclick, setIsShareLinkclick } = useGetSharePaymentLink({
    channel: selectedPaymentLinkChannel ?? defaultChannel,
    id,
    isPass: false,
  });
  const { data: eventChannels } = useGetChannels({
    eventId,
    enabled: isSharedSubMenuClicked && eventId !== undefined,
  });
  const { data: offersChannels } = useGetActiveChannels({
    offerId: id,
    enabled: isSharedSubMenuClicked,
  });

  const handleEdit = useCallback(() => {
    if (onEdit) onEdit(id);
    if (onClick) onClick();
  }, [onEdit, onClick, id]);

  const handleDuplicate = useCallback(() => {
    if (onDuplicate) onDuplicate();
  }, [onDuplicate]);

  const handleDelete = useCallback(() => {
    if (onDelete) onDelete(id);
  }, [onDelete, id]);

  const handleUnassign = useCallback(() => {
    if (onUnassign) onUnassign(id);
  }, [onUnassign, id]);

  const resetSharePaymentLinkStates = () => {
    setSearchPaymentLinkChannelsValue('');
    setSelectedPaymentLinkChannel(undefined);
    setIsSharedSubMenuClicked(false);
    setPaymentLinkChannels(undefined);
    queryClient.invalidateQueries([
      {
        queryKey: ['offer-channels-by-id', id],
      },
      {
        queryKey: ['offer-channels-by-id', eventId],
      },
    ]);
  };

  const handleCloseSelectPaymentLinkChannelModal = () => {
    setIsOpenSelectPaymentLinkChannelModal(false);
  };

  const handleClickSharedLink = useCallback(() => {
    setIsSharedSubMenuClicked(true);
  }, []);

  useEffect(() => {
    if (!isShareLinkclick) {
      if (isOpenSelectPaymentLinkChannelModal) handleCloseSelectPaymentLinkChannelModal();
      else resetSharePaymentLinkStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShareLinkclick]);

  useEffect(() => {
    if (isSharedSubMenuClicked) {
      const enabledEventChannels = eventChannels?.filter((eventChannel) => eventChannel.isEnabled);
      if (offersChannels?.channels.length === 0) setPaymentLinkChannels(enabledEventChannels);
      else {
        const channels = enabledEventChannels?.filter((eventChannel) => {
          return offersChannels?.channels.some((offerChannel) => offerChannel.id === eventChannel.channel.id);
        });
        setPaymentLinkChannels(channels);
      }
      setIsShareLinkclick(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharedSubMenuClicked, eventChannels, offersChannels]);

  const handleShareLink = useCallback(() => {
    setIsShareLinkclick(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsShareLinkclick]);

  const setDefaultPaymentLinkChannel = (channels: EventChannel[]) => {
    const defaultChannelIndex = channels.findIndex((paymentLinkChannel) => {
      const hasDefaultChannel = paymentLinkChannel.channel.id === defaultChannel?.id;
      return hasDefaultChannel;
    });
    if (defaultChannelIndex >= 0) setSelectedPaymentLinkChannel(channels[defaultChannelIndex].channel);
    else setSelectedPaymentLinkChannel(undefined);
  };

  useEffect(() => {
    if (paymentLinkChannels) {
      if (paymentLinkChannels.length === 1) {
        const channel = paymentLinkChannels[0].channel;
        setSelectedPaymentLinkChannel(channel);
      } else if (!selectedPaymentLinkChannel) {
        setDefaultPaymentLinkChannel(paymentLinkChannels);
      }

      setIsOpenSelectPaymentLinkChannelModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentLinkChannels]);

  const handleSearchPaymentLinkChange = (value: string) => {
    setSearchPaymentLinkChannelsValue(value);
  };

  const ACTIONS = useMemo(() => {
    const actionHandlers = [{ name: t('common:edit'), action: handleEdit }];

    if (!isPreset) actionHandlers.push({ name: t('common:duplicate'), action: handleDuplicate });
    if (!isPastEvent) actionHandlers.push({ name: t('common:delete'), action: handleDelete });
    if (isOpenForAll) actionHandlers.push({ name: t('common:sharePaymentLink'), action: handleClickSharedLink });
    if (deleteIsUnassign) actionHandlers.push({ name: t('common:unassign'), action: handleUnassign });

    return actionHandlers;
  }, [
    t,
    handleEdit,
    isPreset,
    handleDuplicate,
    isPastEvent,
    handleDelete,
    isOpenForAll,
    handleClickSharedLink,
    deleteIsUnassign,
    handleUnassign,
  ]);

  const cardOpacity = status === ADMISSION_STATUS_HIDDEN || status === ADMISSION_STATUS_IN_REVIEW ? 'opacity-50' : '';

  return (
    <>
      <div
        className='bg-surface-primary shadow-md rounded-md p-4 flex flex-row justify-between cursor-pointer dark:shadow-black-1000/30'
        style={style}
        ref={setNodeRef}
        role='menuitem'
        tabIndex={0}
        onClick={handleEdit}
        onKeyDown={undefined}>
        <div className='flex flex-row overflow-hidden w-full'>
          <AdmissionCardInfo
            {...{
              id,
              quantity,
              soldItems,
              tags,
              status,
              locale,
              guestCapacity,
              accessValidity,
              checked,
              isPreset,
              listeners,
              attributes,
              isMultiSelectActive,
              onChange,
              cardOpacity,
              color,
              name,
              description,
              isDateVisible,
              onlinePrice,
              ddpPrice,
              formattedSalesEndsDate,
              formattedOnlinePrice,
              formatedDdpPrice,
            }}
          />
        </div>
        <div className='flex flex-col-reverse justify-between sm:flex-row items-center space-x-2 lg:space-x-10'>
          <div className={`text-right text-subtitle-m text-primary  ${cardOpacity}`}>{formattedPrice}</div>
          {showSubmenuIcon && <SubmenuIcon options={ACTIONS} dotsClasses='w-6 h-6 md:mt-1' />}
        </div>
      </div>
      <SearchItemModal
        handleClose={handleCloseSelectPaymentLinkChannelModal}
        isOpen={isOpenSelectPaymentLinkChannelModal}
        placeholder={t('offers:admissionTicketSalesChannelsSearchLabel')}
        title={t('offers:selectChannelModalTitle')}
        buttonPrimaryText={t('common:doneButton')}
        buttonSecondaryText={t('common:discard')}
        isButtonPrimaryDisabled={!selectedPaymentLinkChannel}
        handlePrimaryButton={handleShareLink}
        handleSecondaryButton={handleCloseSelectPaymentLinkChannelModal}
        handleSearch={handleSearchPaymentLinkChange}
        showCloseIconButton={true}
        afterLeave={resetSharePaymentLinkStates}
        debounceInputVariant={DEBOUNCE_VARIANT_DARK}>
        <ChannelList
          handleChange={setSelectedPaymentLinkChannel}
          selectedChannel={selectedPaymentLinkChannel}
          channels={paymentLinkChannels}
          channelSearchValue={searchPaymentLinkChannelsValue}
        />
      </SearchItemModal>
    </>
  );
};
